/**
 * ShareModal Component
 * Modal for previewing and customizing share content before sending to WhatsApp
 */

import React, { useState, useCallback, useEffect } from 'react';
import { ShareData, whatsappService } from '../services/WhatsAppService';
import { messageTemplates, TemplateCustomization } from '../services/MessageTemplates';
import { notificationService, NotificationType } from '../services/NotificationService';
import { ResponsiveModal } from './ResponsiveModal';
import { ShareButton } from './ShareButton';
import './ShareModal.css';

export interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  shareType: 'room' | 'result' | 'challenge' | 'invite' | 'achievement';
  shareData: any; // The data to be shared (room, result, etc.)
  onShareComplete?: (success: boolean) => void;
  className?: string;
}

export const ShareModal: React.FC<ShareModalProps> = ({
  isOpen,
  onClose,
  shareType,
  shareData,
  onShareComplete,
  className = ''
}) => {
  const [message, setMessage] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [includeEmojis, setIncludeEmojis] = useState(true);
  const [tone, setTone] = useState<'casual' | 'competitive' | 'friendly'>('friendly');
  const [isSharing, setIsSharing] = useState(false);
  const [showPrivacySettings, setShowPrivacySettings] = useState(false);
  const [allowDataSharing, setAllowDataSharing] = useState(true);

  // Debug logging
  useEffect(() => {
    console.log('ShareModal Debug:', {
      isOpen,
      shareType,
      shareData,
      hasMessage: !!message,
      messageLength: message.length
    });
  }, [isOpen, shareType, shareData, message]);

  const getShareUrl = useCallback((): string | undefined => {
    // This would be generated by the DeepLinkHandler in a real implementation
    const baseUrl = window.location.origin;
    
    switch (shareType) {
      case 'room':
        // Use gameType from shareData to generate correct URL path
        const gamePath = shareData?.gameType || 'tic-tac-toe';
        return `${baseUrl}/${gamePath}?roomCode=${shareData?.roomCode}`;
      case 'challenge':
        return `${baseUrl}?action=accept-challenge&game=${shareData?.gameType}`;
      case 'result':
        return `${baseUrl}?action=view-result&game=${shareData?.gameType}`;
      default:
        return baseUrl;
    }
  }, [shareType, shareData]);

  const getShareTitle = useCallback((): string => {
    switch (shareType) {
      case 'room':
        return `Join ${shareData?.gameType} Game`;
      case 'result':
        return `${shareData?.gameType} Game Result`;
      case 'challenge':
        return `${shareData?.gameType} Challenge`;
      case 'achievement':
        return `${shareData?.gameType} Achievement`;
      default:
        return 'PlayHub Games AI';
    }
  }, [shareType, shareData]);

  // Generate initial message when modal opens or settings change
  useEffect(() => {
    if (!isOpen) return;

    // Fallback if shareData is null
    if (!shareData) {
      console.warn('ShareModal: shareData is null');
      setMessage('Join my game!');
      return;
    }

    const customizations: TemplateCustomization = {
      includeEmojis,
      tone,
      customMessage: customMessage.trim() || undefined
    };

    let generatedMessage = '';

    try {
      switch (shareType) {
        case 'room':
          generatedMessage = messageTemplates.generateRoomInvite(shareData, customizations);
          break;
        case 'result':
          generatedMessage = messageTemplates.generateGameResult(shareData, customizations);
          break;
        case 'challenge':
          generatedMessage = messageTemplates.generateChallenge(shareData, customizations);
          break;
        case 'invite':
          generatedMessage = messageTemplates.generateGeneralInvite(customizations);
          break;
        case 'achievement':
          generatedMessage = messageTemplates.generateAchievementShare(
            shareData.achievement,
            shareData.gameType,
            customizations
          );
          break;
        default:
          generatedMessage = 'Check out PlayHub Games AI!';
      }
    } catch (error) {
      console.error('Error generating message:', error);
      generatedMessage = 'Join my game!';
    }

    // Append URL and "Powered by" text to show complete message in preview
    const url = getShareUrl();
    if (url) {
      generatedMessage = `${generatedMessage}\n${url}\n\n🎮 Powered by PlayHub Games AI`;
    }

    setMessage(generatedMessage);
  }, [isOpen, shareType, shareData, includeEmojis, tone, customMessage, getShareUrl]);

  const handleShare = useCallback(async () => {
    if (isSharing) return;

    try {
      setIsSharing(true);

      const finalShareData: ShareData = {
        message,
        title: getShareTitle(),
        url: getShareUrl()
      };

      const success = await whatsappService.shareToWhatsApp(finalShareData);
      
      onShareComplete?.(success);
      
      if (success) {
        onClose();
        notificationService.addNotification({
          type: NotificationType.SUCCESS,
          title: 'Shared Successfully!',
          message: 'Your content has been shared to WhatsApp.',
          duration: 3000
        });
      }
    } catch (error) {
      console.error('Share failed:', error);
      notificationService.addNotification({
        type: NotificationType.ERROR,
        title: 'Share Failed',
        message: 'Unable to share content. Please try again.',
        duration: 4000
      });
    } finally {
      setIsSharing(false);
    }
  }, [message, isSharing, onShareComplete, onClose, getShareTitle, getShareUrl]);

  const handleMessageEdit = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(event.target.value);
  }, []);

  const handleCustomMessageChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setCustomMessage(event.target.value);
  }, []);

  const resetToTemplate = useCallback(() => {
    setCustomMessage('');
    // This will trigger the useEffect to regenerate the message
  }, []);

  const getCharacterCount = useCallback(() => {
    return message.length;
  }, [message]);

  const isMessageTooLong = useCallback(() => {
    return message.length > 4000; // WhatsApp limit with some buffer
  }, [message]);

  const getShareTypeIcon = useCallback(() => {
    switch (shareType) {
      case 'room':
        return '🎮';
      case 'result':
        return '🏆';
      case 'challenge':
        return '⚔️';
      case 'achievement':
        return '🎖️';
      default:
        return '🚀';
    }
  }, [shareType]);

  const getShareTypeTitle = useCallback(() => {
    switch (shareType) {
      case 'room':
        return 'Share Game Room';
      case 'result':
        return 'Share Game Result';
      case 'challenge':
        return 'Send Challenge';
      case 'achievement':
        return 'Share Achievement';
      default:
        return 'Share Invitation';
    }
  }, [shareType]);

  if (!isOpen) return null;

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      title={getShareTypeTitle()}
      className={`share-modal ${className}`}
      size="medium"
    >
      <div className="share-modal__content">
        {/* Header with icon and title */}
        <div className="share-modal__header">
          <span className="share-modal__icon">{getShareTypeIcon()}</span>
          <h3 className="share-modal__title">{getShareTypeTitle()}</h3>
        </div>

        {/* Message Preview */}
        <div className="share-modal__preview-section">
          <label className="share-modal__label">
            Message Preview
          </label>
          <div className="share-modal__preview">
            <div className="share-modal__preview-content">
              <textarea
                className={`share-modal__message-textarea ${isMessageTooLong() ? 'share-modal__message-textarea--error' : ''}`}
                value={message}
                onChange={handleMessageEdit}
                placeholder="Your message will appear here..."
                rows={8}
              />
              <div className={`share-modal__character-count ${isMessageTooLong() ? 'share-modal__character-count--error' : ''}`}>
                {getCharacterCount()}/4000 characters
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="share-modal__actions">
          <div className="share-modal__share-options">
            {/* WhatsApp Share */}
            <button
              type="button"
              className="share-modal__share-option share-modal__share-option--whatsapp"
              disabled={isMessageTooLong() || isSharing}
              onClick={async () => {
                setIsSharing(true);
                try {
                  const finalShareData: ShareData = {
                    message,
                    title: getShareTitle(),
                    url: getShareUrl()
                  };
                  const success = await whatsappService.shareToWhatsApp(finalShareData);
                  onShareComplete?.(success);
                  if (success) {
                    onClose();
                    notificationService.addNotification({
                      type: NotificationType.SUCCESS,
                      title: 'Shared to WhatsApp!',
                      message: 'Your content has been shared.',
                      duration: 3000
                    });
                  }
                } catch (error) {
                  console.error('WhatsApp share failed:', error);
                } finally {
                  setIsSharing(false);
                }
              }}
            >
              <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.465 3.516"/>
              </svg>
              WhatsApp
            </button>

            {/* Messenger Share */}
            <button
              type="button"
              className="share-modal__share-option share-modal__share-option--messenger"
              disabled={isMessageTooLong() || isSharing}
              onClick={async () => {
                setIsSharing(true);
                try {
                  const url = getShareUrl() || window.location.origin;
                  const messengerUrl = `fb-messenger://share?link=${encodeURIComponent(url)}&app_id=YOUR_APP_ID`;
                  
                  // Try native messenger first, fallback to web
                  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
                  if (isMobile) {
                    window.location.href = messengerUrl;
                  } else {
                    window.open(`https://www.facebook.com/dialog/send?link=${encodeURIComponent(url)}&app_id=YOUR_APP_ID&redirect_uri=${encodeURIComponent(window.location.origin)}`, '_blank');
                  }
                  
                  onShareComplete?.(true);
                  onClose();
                  notificationService.addNotification({
                    type: NotificationType.SUCCESS,
                    title: 'Opening Messenger...',
                    message: 'Share your content via Messenger.',
                    duration: 3000
                  });
                } catch (error) {
                  console.error('Messenger share failed:', error);
                } finally {
                  setIsSharing(false);
                }
              }}
            >
              <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                <path d="M12 0C5.373 0 0 4.975 0 11.111c0 3.497 1.745 6.616 4.472 8.652V24l4.086-2.242c1.09.301 2.246.464 3.442.464 6.627 0 12-4.974 12-11.111C24 4.975 18.627 0 12 0zm1.191 14.963l-3.055-3.26-5.963 3.26L10.732 8l3.131 3.259L19.752 8l-6.561 6.963z"/>
              </svg>
              Messenger
            </button>

            {/* SMS Share */}
            <button
              type="button"
              className="share-modal__share-option share-modal__share-option--sms"
              disabled={isSharing}
              onClick={async () => {
                setIsSharing(true);
                try {
                  const url = getShareUrl() || window.location.origin;
                  const smsBody = `${message}\n\n${url}`;
                  
                  // iOS uses different separator than Android
                  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
                  const separator = isIOS ? '&' : '?';
                  const smsUrl = `sms:${separator}body=${encodeURIComponent(smsBody)}`;
                  
                  window.location.href = smsUrl;
                  
                  onShareComplete?.(true);
                  onClose();
                  notificationService.addNotification({
                    type: NotificationType.SUCCESS,
                    title: 'Opening Messages...',
                    message: 'Share your content via SMS.',
                    duration: 3000
                  });
                } catch (error) {
                  console.error('SMS share failed:', error);
                  notificationService.addNotification({
                    type: NotificationType.ERROR,
                    title: 'SMS Failed',
                    message: 'Unable to open SMS app. Please try another method.',
                    duration: 3000
                  });
                } finally {
                  setIsSharing(false);
                }
              }}
            >
              <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM9 11H7V9h2v2zm4 0h-2V9h2v2zm4 0h-2V9h2v2z"/>
              </svg>
              SMS
            </button>

            {/* Copy Link */}
            <button
              type="button"
              className="share-modal__share-option share-modal__share-option--copy"
              disabled={isSharing}
              onClick={async () => {
                setIsSharing(true);
                try {
                  const url = getShareUrl() || window.location.origin;
                  const textToCopy = shareType === 'room' 
                    ? `${message}\n\n${url}`
                    : url;
                  
                  await navigator.clipboard.writeText(textToCopy);
                  
                  onShareComplete?.(true);
                  notificationService.addNotification({
                    type: NotificationType.SUCCESS,
                    title: 'Link Copied!',
                    message: 'The link has been copied to your clipboard.',
                    duration: 3000
                  });
                } catch (error) {
                  console.error('Copy failed:', error);
                  notificationService.addNotification({
                    type: NotificationType.ERROR,
                    title: 'Copy Failed',
                    message: 'Unable to copy link. Please try again.',
                    duration: 3000
                  });
                } finally {
                  setIsSharing(false);
                }
              }}
            >
              <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
              </svg>
              Copy Link
            </button>
          </div>
          
          <button
            type="button"
            className="share-modal__cancel-button"
            onClick={onClose}
            disabled={isSharing}
          >
            Cancel
          </button>
        </div>
      </div>
    </ResponsiveModal>
  );
};

export default ShareModal;